import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDebug } from '../../contexts/DebugContext';
import { jobPath } from '../../nav/paths';

// Shared UI building blocks so every screen looks and behaves the same.
// Icons are plain characters (no icon library); every icon button has a title
// (tooltip) and aria-label.

export const ICONS = {
  refresh: '↻',   // ↻
  back: '←',      // ←
  prev: '‹',      // ‹
  next: '›',      // ›
  close: '×',     // ×
  edit: '✎',      // ✎
  save: '✔',      // ✔
  cancel: '✕',    // ✕
  add: '+',
  expand: '▸',    // ▸
  collapse: '▾',  // ▾
  expandAll: '⊞', // ⊞
  collapseAll: '⊟', // ⊟
  drag: '⠿',      // ⠿ drag handle
  wider: '↔',     // ↔ double width
  taller: '↕',    // ↕ double height
  external: '↗',  // ↗
  settings: '⚙',  // ⚙
  dashboard: '▦', // ▦
  help: '?',
  logout: '⏻',    // ⏻
  sortUp: '▲',    // ▲
  sortDown: '▼',  // ▼
  delete: '\u{1F5D1}', // 🗑
  receipts: '\u{1F9FE}', // 🧾
  timeNotes: '\u{1F4DD}', // 📝
  excel: '\u{1F4CA}',     // 📊 export to Excel
  favorite: '☆',  // ☆ save as a favorite
  fullScreen: '⤢', // ⤢ show full screen
};

// Small icon-only button. `icon` is an ICONS key; `title` is required (tooltip).
export function IconButton({ icon, title, onClick, disabled, active, className = '', style, type = 'button' }) {
  return (
    <button type={type} className={`icon-btn${active ? ' active' : ''} ${className}`.trim()} title={title} aria-label={title}
      onClick={onClick} disabled={disabled} style={style}>
      {ICONS[icon] || icon}
    </button>
  );
}

// Status message (kind: 'ok' | 'err' | 'warn' | 'info') with an × to close it.
export function Banner({ kind = 'info', onClose, children, style }) {
  if (!children) return null;
  return (
    <div className={`banner banner-${kind}`} role={kind === 'err' ? 'alert' : 'status'} style={style}>
      <div className="banner-text">{children}</div>
      {onClose && <IconButton icon="close" title="Close" onClick={onClose} className="banner-close" />}
    </div>
  );
}

// Screen header: optional back / previous / next, the title, a position label
// ("3 of 12"), extra actions, and the refresh icon — always in the same places.
export function PageHeader({ title, subtitle, onBack, backTitle = 'Back to list', onPrev, onNext, prevTitle = 'Previous',
  nextTitle = 'Next', position, onRefresh, refreshing, actions, center }) {
  return (
    <div className={`page-header${center ? ' page-header-center' : ''}`}>
      <div className="page-header-nav">
        {onBack && <IconButton icon="back" title={backTitle} onClick={onBack} />}
        {(onPrev !== undefined || onNext !== undefined) && (
          <>
            <IconButton icon="prev" title={prevTitle} onClick={onPrev || undefined} disabled={!onPrev} />
            <IconButton icon="next" title={nextTitle} onClick={onNext || undefined} disabled={!onNext} />
          </>
        )}
        {position && <span className="page-header-position">{position}</span>}
      </div>
      <div className="page-header-title">
        {title && <h2>{title}</h2>}
        {subtitle && <div className="page-header-subtitle">{subtitle}</div>}
      </div>
      <div className="page-header-actions">
        {actions}
        {onRefresh && <IconButton icon="refresh" title="Refresh" onClick={onRefresh} disabled={refreshing} />}
      </div>
    </div>
  );
}

// Content that may use the full screen width on large screens while staying the
// normal width on phones. By default it is as wide as its content (centered);
// with `fill` it takes the available width up to `maxWidth` (for grids whose
// columns should spread out, like the tabular timesheet).
export function WideSection({ children, style, fill, maxWidth }) {
  return (
    <div className="wide-section" style={style}>
      <div className={`wide-section-inner${fill ? ' wide-section-fill' : ''}`} style={fill && maxWidth ? { maxWidth } : undefined}>{children}</div>
    </div>
  );
}

// ↗ link to a job's job page (all its tabs: Budget, Entries, Create Invoices,
// Invoice Approval, Receivables). Shown next to a job number on list screens;
// hidden when already on that job's page.
export function JobPageLink({ jobNo, style }) {
  const { pathname } = useLocation();
  const no = String(jobNo || '').trim();
  const to = jobPath(no);
  if (!no || pathname === to || pathname.startsWith(`${to}/`)) return null;
  return (
    <Link to={to} className="job-page-link" title={`Open the job page for ${no}`} aria-label={`Open the job page for ${no}`}
      onClick={(e) => e.stopPropagation()} style={style}>
      {ICONS.external}
    </Link>
  );
}

// Debug-mode panel: the Navision API calls (request, response, timing) the
// server returned in _navDebug, collapsed by default. Renders nothing when debug
// mode is off or there are no calls.
export function DebugPanel({ calls, title = 'Navision API calls' }) {
  const { debugMode } = useDebug();
  if (!debugMode || !calls || !calls.length) return null;
  return (
    <details className="debug-panel">
      <summary>{title} ({calls.length})</summary>
      <pre>{JSON.stringify(calls, null, 2)}</pre>
    </details>
  );
}

// The standard "Export to Excel" icon button. onExport does the export (usually
// utils/exportExcel); the button shows busy while it runs and alerts on failure.
export function ExportButton({ onExport, title = 'Export to Excel', disabled }) {
  const [busy, setBusy] = React.useState(false);
  const run = async () => {
    setBusy(true);
    try { await onExport(); } catch (e) { alert((e && e.message) || 'Export failed'); } finally { setBusy(false); }
  };
  return <IconButton icon="excel" title={busy ? 'Exporting…' : title} onClick={run} disabled={disabled || busy} />;
}

// Up / down sort arrows for a column header. sort = { key, dir: 1 | -1 }.
export function SortArrows({ sort, sortKey, onSort, label }) {
  const btn = (dir) => {
    const active = sort.key === sortKey && sort.dir === dir;
    return (
      <button type="button" className={`sort-arrow${active ? ' active' : ''}`}
        title={`Sort${label ? ` ${label}` : ''} ${dir === 1 ? 'ascending' : 'descending'}`}
        onClick={() => onSort({ key: sortKey, dir })}>
        {dir === 1 ? ICONS.sortUp : ICONS.sortDown}
      </button>
    );
  };
  return (
    <span className="sort-arrows">
      {label && <span className="sort-arrows-label">{label}</span>}
      {btn(1)}
      {btn(-1)}
    </span>
  );
}

// Filter buttons with counts. options = [{ key, label, count? }].
export function FilterButtons({ options, value, onChange }) {
  return (
    <div className="filter-buttons">
      {options.map((o) => (
        <button key={o.key} type="button" className={`btn btn-sm ${value === o.key ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => onChange(o.key)}>
          {o.label}{o.count != null ? ` (${o.count})` : ''}
        </button>
      ))}
    </div>
  );
}
